import { useEffect, useRef, useState } from 'react';
import { UploadStatus } from '@shared/lib/uploads';
import { useAppDispatch, useAppSelector } from '@shared/store';
import { toast } from '@shared/ui';
import { TEXTS } from '../texts';
import { areVideoMediafileUploadsEqual, selectVideoMediafileUploads } from './selectors';
import {
  removeCompletedVideoMediafileUploads,
  removeVideoMediafileUpload,
  retryVideoMediafileUpload,
  uploadVideoMediafile,
} from './thunks';
import type { VideoMediafileUploadsProps } from './types';

export const useVideoMediafileUploads = ({ companyId, refetch, query }: VideoMediafileUploadsProps) => {
  const dispatch = useAppDispatch();
  const owned = useAppSelector<ReturnType<typeof selectVideoMediafileUploads>>(
    (state) => selectVideoMediafileUploads(state, companyId),
    areVideoMediafileUploadsEqual
  );
  const completed = owned.filter((item) => item.status === UploadStatus.SUCCEEDED);
  const completedKey = JSON.stringify(completed.map(({ id, requestId }) => [id, requestId]));
  const currentRefetch = useRef(refetch);
  const activeCompany = useRef<string | undefined>(undefined);
  const scope = JSON.stringify([companyId, query ?? '']);
  const currentScope = useRef(scope);
  currentScope.current = scope;
  const [refreshErrors, setRefreshErrors] = useState<string[]>([]);
  const [retryRead, setRetryRead] = useState(0);
  currentRefetch.current = refetch;

  useEffect(() => {
    activeCompany.current = companyId;
    return () => {
      activeCompany.current = undefined;
    };
  }, [companyId]);

  useEffect(() => {
    let isCurrent = true;
    setRefreshErrors([]);
    const finished = JSON.parse(completedKey) as [string, string][];
    if (finished.length) {
      currentRefetch
        .current()
        .then(() => {
          if (isCurrent && currentScope.current === scope)
            dispatch(removeCompletedVideoMediafileUploads(companyId, finished));
        })
        .catch(() => {
          if (isCurrent && currentScope.current === scope) setRefreshErrors(finished.map(([id]) => id));
        });
    }
    // Closing/changing this view detaches its read, not the store-owned uploads.
    return () => {
      isCurrent = false;
    };
  }, [companyId, scope, completedKey, retryRead, dispatch]);

  const upload = (files: File[]) => {
    // Redux insertion order is old-first; reverse the view, preserving order within a new batch.
    [...files].reverse().forEach((file) => {
      const request = dispatch(uploadVideoMediafile(companyId, file));
      request.unwrap().catch((error: unknown) => {
        if (activeCompany.current !== companyId) return;
        dispatch((_dispatch, getState) => {
          if (getState().uploads.items[request.arg.id]?.requestId !== request.requestId) return;
          const message = String(error);
          toast.error(/already exists/.test(message) ? TEXTS.DUPLICATE_MEDIAFILE_ERROR : message);
        });
      });
    });
  };

  const retry = (id: string) => {
    if (refreshErrors.includes(id)) setRetryRead((value) => value + 1);
    else dispatch(retryVideoMediafileUpload(companyId, id));
  };

  const items = [...owned].reverse().map(({ id, fileName, status }) => {
    let uploadState: 'uploading' | 'refresh-error' | 'upload-error' = 'uploading';
    if (status === UploadStatus.FAILED) uploadState = 'upload-error';
    if (refreshErrors.includes(id)) uploadState = 'refresh-error';
    return { id, title: fileName, uploading: uploadState === 'uploading', uploadState };
  });

  return {
    items,
    upload,
    retry,
    remove: (id: string) => dispatch(removeVideoMediafileUpload(companyId, id)),
    uploadLoading: items.some((item) => item.uploading),
  };
};
